<template>
    <!-- Enhanced Audit Details Slide Over -->
    <div>
        <slide-over-panel   v-model:show="showEnhancedAuditDetailsSlideOver"
                            width="sm:max-w-2xl md:max-w-2xl lg:max-w-3xl xl:max-w-5xl">
            <template   #content>
                <enhanced-audit-details     :audit="selectedEnhancedAudit"
                                            :show-notes="showNotes"
                >
                </enhanced-audit-details>
            </template>
        </slide-over-panel>
    </div>

    <!-- Enhanced Audit Filters -->
    <div>
        <slide-over-panel   v-model:show="showFilterSlideOver">
            <template   #content>
                <enhanced-audit-index-filters   v-model:auditable-type-filter="auditableTypeFilter"
                                                v-model:auditable-id-filter="auditableIdFilter"
                                                v-model:user-filter="userFilter"
                                                :auditable-types="auditableTypes"
                                                :auditable-ids="auditableIds"
                                                :auditable-ids-for-models="enhancedAuditIdsForAllModels"
                                                :users="users"
                                                @auditable-type-filter-changed="auditableTypeFilterChanged"
                                                @model-instance-id-changed="auditableIdFilterChanged"
                                                @user-filter-changed="userFilterChanged"
                                                @filters-changed="getData"
                ></enhanced-audit-index-filters>
            </template>
        </slide-over-panel>
    </div>

    <!-- Main Enhanced Audit Table -->
    <div    class="w-full flex justify-center items-center">
        <div    class="w-3/4">
            <h1>Enhanced Audit Index</h1>

            <ajax-table     v-model:items-per-page="ajaxTable.itemsPerPage"
                            v-model:loading="ajaxTable.loading"
                            v-model:page="ajaxTable.page"
                            v-model:page-info="ajaxTable.pageInfo"
                            v-model:search="ajaxTable.search"
                            v-model:sort-by="ajaxTable.sortBy"
                            v-model:sort-desc="ajaxTable.sortDesc"
                            :data="data"
                            :exportable="false"
                            :headers="headers"
                            @loadData="getData"
                            @row-selected="selectEnhancedAudit"
            >
                <!-- Show Filters Button -->
                <template #above_table_center>
                    <div class="flex flex-row items-center justify-start gap-4">
                        <button-blue-secondary class="py-3 max-w-none sm:max-w-48" @click="showFilterSlideOver=true">
                            <span class="inline sm:hidden lg:inline mr-1 sm:mr-0 lg:mr-1">Show</span> Filters
                        </button-blue-secondary>
                        <button-blue-secondary class="py-3 max-w-none sm:max-w-48" @click="exportEnhancedAudit">Export</button-blue-secondary>
                    </div>
                </template>
                
                <!-- User and Email -->
                <template   #col-2="props">
                    <div    class="flex flex-col items-start justify-center">
                        <span   class="my-1 text-gray-700">
                            {{ props.row.user_display_name }}
                        </span>
                        <span   class="my-1 text-gray-500">
                            {{ props.row.user_email }}
                        </span>
                    </div>
                </template>

                <!-- Model Information -->
                <template   #col-3="props">
                    <div    class="flex flex-col items-start justify-center">
                        <!-- Pretty Auditable Type -->
                        <span   class="my-1 text-gray-700">
                            {{ props.row.auditable_type_pretty }}
                        </span>
                        <!-- Model pretty name/ descriptor -->
                        <span   class="my-1 text-gray-500">
                            {{ props.row.auditable_name_pretty }}
                        </span>
                    </div>
                </template>

                <!-- Changes -->
                <template   #col-4="props">
                    <!-- Multiple Changes, should list just the changed columns -->
                    <div    v-if="props.row.changes.length > 1"
                            class="">
                        <div>
                            <span   v-for="(change, index) in props.row.changes">
                                {{ change }}{{ index < props.row.changes.length - 1 ? ', ' : '' }}
                            </span>
                        </div>
                    </div>

                    <!-- Single Change, should list the changed column and the changed field as a detail -->
                    <div    v-else
                            class="flex flex-col items-start justify-center">
                        <!-- Changed Column Name -->
                        <span   class="my-1 text-gray-700">
                            {{ props.row.changes[0] }}
                        </span>

                        <!-- Human readable of changes text -->
                        <span   class="my-1 text-gray-500">
<!--                            {{ props.row.changes_human_readable[props.row.changes[0]] }}-->
                            {{ singleChangesHumanReadableText(props.row) }}
                        </span>

                    </div>
                </template>
            </ajax-table>
        </div>
    </div>
</template>

<script>
import {SonicuCoreMixin} from "@/mixins/SonicuCoreMixin";
import {iconNamesMixin} from '@/mixins/IconNamesMixin';
import {ajaxTableMixin} from "@/mixins/ajaxTableMixin";
import AjaxTable from "@/components/elements/AjaxTable";
import SlideOverPanel from "@/components/elements/SlideOverPanel.vue";
import EnhancedAuditDetails from "@/components/EnhancedAudit/EnhancedAuditDetails.vue";

import axios from "axios"
import _ from 'lodash'
import EnhancedAuditIndexFilters from "@/components/EnhancedAudit/EnhancedAuditIndexFilters.vue";
import ButtonBlueSecondary from "@/components/elements/buttons/ButtonBlueSecondary.vue";
import {UrlParamsMixin} from "@/mixins/UrlParamsMixin";

export default {
    name: "EnhancedAuditIndex",
    mixins: [SonicuCoreMixin, iconNamesMixin, ajaxTableMixin, UrlParamsMixin],
    components: {ButtonBlueSecondary, EnhancedAuditIndexFilters, EnhancedAuditDetails, SlideOverPanel, AjaxTable},
    props: {
        showNotes: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            data: [],
            headers: [
                {
                    name: 'Time',
                    key: 'created_at_pretty',
                    sortBy: 'created_at',
                    show: 'sm',
                },
                {
                    name: 'User',
                    key: 'user_display_name',
                    sortBy: 'user_display_name',
                    show: 'sm',
                },
                {
                    name: 'Auditable Type',
                    key: 'auditable_type_pretty',
                    sortBy: 'auditable_type',
                    show: 'sm',
                },
                {
                    name: 'Changed Fields',
                    key: 'changes',
                    sortBy: 'changes',
                    show: 'sm',
                },
            ],
            requestNumber: 0,

            // Filters
            auditableTypes: [],
            auditableTypeFilter: null,
            auditableIds: [],
            auditableIdFilter: null,
            users: [],
            userFilter: null,

            // Selected Audit
            enhancedAuditIdsForAllModels: {},

            selectedEnhancedAudit: null,

            // For Modal
            showFilterSlideOver: false,
            showEnhancedAuditDetailsSlideOver: false,
            preLoadAudit: null,

            exportFlag: false
        }
    },
    created() {
        this.ajaxTable.sortBy = 'created_at'
        this.ajaxTable.sortDesc = true

        // Let Me test paging.
        // this.ajaxTable.itemsPerPage = "10"
    },
    mounted() {
        this.preLoadAudit = this.urlParamsGet('id')
        if (this.urlParamsHas('auditable_type')) {
            let auditableType = this.urlParamsGet('auditable_type')

            // Replace the text encoding for backslashes with a single backslash.  Sometimes it is double encoded; so we need to reduce it to 1 backslash.
            auditableType = _.replace(auditableType, /%5c/gi, '\\')
            auditableType = _.replace(auditableType, /%5C/gi, '\\')
            auditableType = _.replace(auditableType, /\\\\/gi, '\\')
            this.auditableTypeFilter = auditableType
        }
        if (this.urlParamsHas('auditable_id')) {
            this.auditableIdFilter = this.urlParamsGet('auditable_id')
        }
        if (this.urlParamsHas('user')) {
            this.userFilter = this.urlParamsGet('user')
        }
    },
    methods: {
        getData() {
            this.ajaxTable.loading = true
            this.requestNumber++
            let params = this.constructParamsForRequest()

            axios.get(this.company.buildCompanyApiInternalRoute('enhanced-audits'),
                    {
                        params: params,
                    }
            ).then(response => {
                // Grab Data and if meta doesn't exist or request number doesn't match, quit out.
                let data = response.data
                if (_.isNil(data.meta) || data.meta.requestNumber !== `${this.requestNumber}`) {
                    return
                }

                // Update Table Data to AjaxTable
                this.ajaxUpdateTableData(data)
                this.data = ! _.isNil(data.data) ? data.data : []

                // Update filter options with what is returned from route
                this.auditableTypes = ! _.isNil(data.meta.auditableTypes) ? data.meta.auditableTypes : []
                this.enhancedAuditIdsForAllModels = ! _.isNil(data.meta.auditableTypesInstances) ? data.meta.auditableTypesInstances : {}
                this.updateAuditableIds()
                this.users = ! _.isNil(data.meta.users) ? data.meta.users : []


                // If we should preload the template, make it active
                if (! _.isNil(data.meta.enhancedAudit)) {
                    this.selectedEnhancedAudit = data.meta.enhancedAudit
                    this.showEnhancedAuditDetailsSlideOver = true
                }
                else {
                    this.preLoadAudit = null
                }

            }).catch(error => {
                console.log(error)
            }).then(() => {
                this.ajaxTable.loading = false
                this.exportFlag = false
            })
        },
        selectEnhancedAudit(rowIndex) {
            this.updateSelectedEnhancedAudit(rowIndex)
            if (_.isNil(this.selectedEnhancedAudit)) {
                return
            }

            this.showEnhancedAuditDetailsSlideOver = true
        },
        auditableTypeFilterChanged(auditableType) {
            this.auditableTypeFilter = auditableType
            this.showFilterSlideOver = false
            if (_.isNil(this.auditableTypeFilter)) {
                this.urlParamsRemove('auditable_type')
            }
            else {
                this.urlParamsStore('auditable_type', this.auditableTypeFilter)
            }

            this.updateAuditableIds()
        },
        auditableIdFilterChanged(auditableId) {
            this.setAuditableIdFilter(auditableId)
            this.showFilterSlideOver = false
            if (_.isNil(this.auditableIdFilter)) {
                this.urlParamsRemove('auditable_id')
            }
            else {
                this.urlParamsStore('auditable_id', this.auditableIdFilter)
            }
        },
        userFilterChanged(user) {
            this.userFilter = user
            this.showFilterSlideOver = false
            if (_.isNil(this.userFilter)) {
                this.urlParamsRemove('user')
            }
            else {
                this.urlParamsStore('user', this.userFilter)
            }
        },
        updateAuditableIds() {
            // If auditableType is in the type=>ids dictionary, populate the id selection
            if (_.has(this.enhancedAuditIdsForAllModels, this.auditableTypeFilter)) {
                this.auditableIds = this.enhancedAuditIdsForAllModels[this.auditableTypeFilter]
            }
            else {
                // auditable type is invalid, clear the id selection.
                this.auditableIds = []
            }
        },
        updateSelectedEnhancedAudit(id = null) {
            if (_.isNil(this.data)) {
                return
            }

            this.selectedEnhancedAudit = this.data[id]
        },
        singleChangesHumanReadableText(row) {
            if (_.isNil(row.changes) || row.changes.length > 1) {
                return ''
            }

            const textLimit = 50
            let changeColumn = row.changes[0]
            let changeValue = row.changes_human_readable[changeColumn]
            return _.size(changeValue) > textLimit
                    ? `${changeValue.substring(0, textLimit)}...`
                    : changeValue
        },
        setAuditableIdFilter(auditableId) {
            // Make sure the auditable id is in the list of auditable ids for the selected auditable type
            if (! _.has(this.enhancedAuditIdsForAllModels, this.auditableTypeFilter)) {
                // The auditable type is not in the list of valid types.  We will quit out.
                if (this.$data.auditableIdFilter === auditableId || _.isNil(auditableId)) {
                    // If auditable id is the same as the selected auditable id, reset selected
                    this.$data.auditableIdFilter = null
                }
                return
            }

            if (_.isNil(auditableId)) {
                this.$data.auditableIdFilter = null
                return
            }

            // set the selected auditable id, should the candidate prove valid.
            this.auditableIdFilter = auditableId
        },
        constructParamsForRequest() {
            let params = JSON.parse(JSON.stringify(this.ajaxTable))
            params.pageInfo = null
            params.requestNumber = this.requestNumber

            if (! _.isNil(this.auditableTypeFilter)) {
                params.auditableTypeFilter = this.auditableTypeFilter
            }

            if (! _.isNil(this.auditableIdFilter)) {
                params.auditableIdFilter = this.auditableIdFilter
            }

            if (! _.isNil(this.userFilter)) {
                params.userFilter = this.user
            }

            if (this.preLoadAudit) {
                params.requestedAudit = this.preLoadAudit
            }

            if (this.exportFlag) {
                params.export = this.exportFlag;
            }

            return params
        },
        exportEnhancedAudit() {
            this.exportFlag = true;
            this.getData();
        }
    },
    computed: {
        auditableType() {
            return this.auditableTypeFilter
        },
        user() {
            return _.isNil(this.users)
                    ? null
                    : this.users[this.userFilter]
        },
    },
    watch: {
        showEnhancedAuditDetailsSlideOver(showModal) {
            // If we are closing the slide over, remove the id from the url params
            if (! showModal) {
                this.preLoadAudit = null
                return
            }

            // If no data was retrieved, remove id from the url params
            if (_.isNil(this.data)) {
                this.preLoadAudit = null
                return
            }

            // Update url params
            if (_.isNil(this.selectedEnhancedAudit)) {
                this.preLoadAudit = null
                this.showEnhancedAuditDetailsSlideOver = false
                return
            }

            this.preLoadAudit = this.selectedEnhancedAudit.id

        },
        preLoadAudit(newId, oldId) {
            if (_.isNil(newId)) {
                // Remove Param
                this.urlParamsRemove('id')
            }
            else {
                // Add Param
                this.urlParamsStore('id', newId)
            }

        },
    },
}
</script>

<style scoped>

</style>